import { useEffect, useRef } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import ecoHero from '@/assets/eco-hero.jpg';
import ecoFarmer from '@/assets/eco-farmer.jpg';
import ecoRuralDev from '@/assets/eco-rural-dev.jpg';
import ecoWhatEthanol from '@/assets/eco-what-ethanol.jpg';

const useScrollReveal = (ref: React.RefObject<HTMLDivElement | null>) => {
  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add('visible'); }),
      { threshold: 0.08 }
    );
    ref.current?.querySelectorAll('.animate-on-scroll').forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, []);
};

const supplyChainSteps = [
  { step: '01', title: 'Feedstock Cultivation', desc: 'Sugarcane and grain crops grown across Bihar\'s fertile districts provide the raw material for ethanol production.', icon: 'ri-plant-line' },
  { step: '02', title: 'Milling & Pre-processing', desc: 'Sugar mills extract juice or starch from feedstock, preparing it for the fermentation process.', icon: 'ri-settings-3-line' },
  { step: '03', title: 'Fermentation', desc: 'Yeast-based fermentation converts sugars into ethanol. Modern distilleries achieve 99.9% purity.', icon: 'ri-flask-line' },
  { step: '04', title: 'Distillation & Dehydration', desc: 'Multi-stage distillation and molecular sieve dehydration produce anhydrous ethanol meeting IS 15464:2004.', icon: 'ri-drop-line' },
  { step: '05', title: 'Quality Testing & Dispatch', desc: 'NABL-accredited labs test ethanol before dispatch. Denaturing ensures safety compliance.', icon: 'ri-test-tube-line' },
  { step: '06', title: 'OMC Blending & Distribution', desc: 'IOCL, BPCL, and HPCL procure and blend ethanol with petrol at depots — achieving up to E20 blend.', icon: 'ri-gas-station-line' },
];

const farmerBenefits = [
  { icon: 'ri-money-rupee-circle-line', title: 'Assured Price Mechanism', desc: 'Farmers receive Fair & Remunerative Prices (FRP) and State Advised Prices (SAP) set annually, guaranteeing predictable income from sugarcane supply.', color: '#28a99e' },
  { icon: 'ri-calendar-check-line', title: 'Year-Round Demand', desc: 'Unlike seasonal crops, ethanol demand is continuous. Distilleries in Bihar source feedstock year-round — eliminating the traditional harvesting income gaps.', color: '#008000' },
  { icon: 'ri-bank-line', title: 'Direct Bank Transfers', desc: 'Payments are routed directly to farmers\' accounts within 14 days of cane delivery — reducing dependence on middlemen and improving cash flow.', color: '#F0C808' },
  { icon: 'ri-group-line', title: 'Farmer Producer Organisations', desc: 'BEA promotes FPO formation in key sugarcane districts to improve collective bargaining, input access, and technology adoption among smallholder farmers.', color: '#28a99e' },
];

const ruralImpactStats = [
  { value: '2.4L+', label: 'Farming families supported', icon: 'ri-user-heart-line', color: '#28a99e' },
  { value: '₹3,800Cr', label: 'Annual farmer income via ethanol chain', icon: 'ri-coins-line', color: '#008000' },
  { value: '48,000+', label: 'Direct & indirect rural jobs created', icon: 'ri-briefcase-line', color: '#F0C808' },
  { value: '18 Districts', label: 'Across Bihar benefit from ethanol sourcing', icon: 'ri-map-2-line', color: '#28a99e' },
];

const ruralDevelopmentPoints = [
  {
    icon: 'ri-seedling-line',
    title: 'Crop Diversification',
    desc: 'Ethanol policy supports cultivation of maize, sugarcane, and broken rice — encouraging farmers to diversify away from mono-cropping patterns that increase climate risk.',
  },
  {
    icon: 'ri-home-8-line',
    title: 'Village Infrastructure',
    desc: 'Distillery investments in sugarcane belts bring with them rural roads, storage godowns, and cooperative infrastructure that benefit whole communities.',
  },
  {
    icon: 'ri-graduation-cap-line',
    title: 'Skill Development',
    desc: 'BEA\'s training programmes upskill local youth in distillery operations, lab testing, and logistics — creating employment in semi-urban areas close to home.',
  },
  {
    icon: 'ri-leaf-line',
    title: 'Sustainable Agri-Practices',
    desc: 'Ethanol-linked agronomy programmes promote drip irrigation, organic inputs, and soil health management — improving long-term agricultural productivity.',
  },
];

const Ecosystem = () => {
  const pageRef = useRef<HTMLDivElement>(null);
  useScrollReveal(pageRef);

  return (
    <div className="font-jakarta bg-white">
      <Navbar />
      <main ref={pageRef}>

        {/* ── HERO ── */}
        <section className="pt-14 bg-white">
          <div className="max-w-4xl mx-auto px-6 md:px-10 pt-14 pb-12 text-center">
            <div className="animate-on-scroll mb-4">
              <span className="text-[11px] font-bold text-bgreen tracking-widest uppercase">Ethanol Ecosystem</span>
            </div>
            <h1 className="animate-on-scroll text-4xl md:text-5xl font-bold text-ink leading-tight tracking-tight mb-4">
              Bihar&apos;s Ethanol Ecosystem
            </h1>
            <p className="animate-on-scroll text-ink-muted text-lg max-w-xl mx-auto leading-relaxed">
              From sugarcane fields to fuel station — the complete ethanol value chain powering Bihar&apos;s green fuel revolution.
            </p>
          </div>
          <div className="max-w-7xl mx-auto px-6 md:px-10">
            <div className="animate-on-scroll rounded-lg overflow-hidden w-full" style={{ height: '340px' }}>
              <img
                src={ecoHero}
                alt="Bihar Ethanol Ecosystem"
                className="w-full h-full object-cover object-center"
              />
            </div>
          </div>
          <div className="h-px bg-gray-100 mt-0" />
        </section>

        {/* ── STATS BAR ── */}
        <section className="py-8 bg-white border-b border-gray-100">
          <div className="max-w-7xl mx-auto px-6 md:px-10">
            <div className="flex flex-wrap items-center gap-0 divide-x divide-gray-100">
              {[
                { value: '22', label: 'Active Distilleries', color: '#28a99e' },
                { value: '3,100 KLPD', label: 'Production Capacity', color: '#008000' },
                { value: '₹2,500Cr+', label: 'Annual Revenue', color: '#28a99e' },
                { value: 'E20', label: 'Blending Target', color: '#F0C808' },
              ].map((s, i) => (
                <div key={i} className="animate-on-scroll px-8 first:pl-0 py-2" style={{ transitionDelay: `${i * 0.08}s` }}>
                  <p className="text-xl font-bold" style={{ color: s.color }}>{s.value}</p>
                  <p className="text-xs text-ink-muted mt-0.5">{s.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── WHAT IS ETHANOL ── */}
        <section className="py-16 md:py-24 bg-white">
          <div className="max-w-7xl mx-auto px-6 md:px-10">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
              {/* Text */}
              <div>
                <span className="text-[11px] font-bold text-bgreen tracking-widest uppercase">Understanding Ethanol</span>
                <h2 className="animate-on-scroll text-3xl md:text-4xl font-bold text-ink mt-2 mb-6 tracking-tight leading-tight">
                  What is Ethanol &amp; Its significance in India&apos;s fuel roadmap
                </h2>
                <p className="animate-on-scroll text-ink-muted text-sm leading-relaxed mb-5">
                  Ethanol (C₂H₅OH) is a biofuel produced by fermenting sugar or starch-rich crops such as sugarcane, maize, and rice — making it a renewable, domestically produced energy source.
                </p>
                <p className="animate-on-scroll text-ink-muted text-sm leading-relaxed mb-5">
                  India imports over 85% of its crude oil requirement, costing the nation over $100 billion annually. Ethanol blending with petrol directly reduces this import burden, improves the current account deficit, and cuts vehicular carbon emissions by up to 30%.
                </p>
                <p className="animate-on-scroll text-ink-muted text-sm leading-relaxed mb-8">
                  The Government of India&apos;s <strong className="text-ink">Ethanol Blended Petrol (EBP) Programme</strong> targets 20% blending by 2025 — a milestone that will require 1,016 crore litres of ethanol annually and represents a strategic shift towards <strong className="text-ink">energy self-reliance</strong>.
                </p>
                <div className="animate-on-scroll grid grid-cols-3 gap-4">
                  {[
                    { value: '20%', label: 'Blend Target by 2025', color: '#28a99e' },
                    { value: '30%', label: 'Reduction in CO₂ emissions', color: '#008000' },
                    { value: '$100B+', label: 'Annual crude import savings potential', color: '#F0C808' },
                  ].map((stat, i) => (
                    <div key={i} className="p-4 rounded-lg border border-gray-100 bg-surface">
                      <p className="text-xl font-black leading-none mb-1" style={{ color: stat.color }}>{stat.value}</p>
                      <p className="text-[11px] text-ink-muted leading-snug">{stat.label}</p>
                    </div>
                  ))}
                </div>
              </div>
              {/* Image */}
              <div className="space-y-4">
                <div className="animate-on-scroll rounded-lg overflow-hidden" style={{ height: '280px' }}>
                  <img
                    src={ecoWhatEthanol}
                    alt="Ethanol Production Plant India"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="animate-on-scroll p-5 rounded-lg bg-bteal/8 border border-bteal/15">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 flex items-center justify-center rounded-md bg-bteal/15 flex-shrink-0 mt-0.5">
                      <i className="ri-leaf-line text-bteal text-sm" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-ink mb-1">National Biofuel Policy 2018</p>
                      <p className="text-xs text-ink-muted leading-relaxed">India&apos;s policy framework mandates the use of domestically produced ethanol, setting phased blending targets and fair pricing — creating a guaranteed offtake market for producers like Bihar&apos;s distilleries.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── VALUE CHAIN / SUPPLY CHAIN ── */}
        <section className="py-16 md:py-24 bg-bteal">
          <div className="max-w-7xl mx-auto px-6 md:px-10">
            <span className="text-[11px] font-bold text-white/50 tracking-widest uppercase">Value Chain</span>
            <h2 className="animate-on-scroll text-3xl font-bold text-white mt-2 mb-3 tracking-tight">The Ethanol Supply Chain</h2>
            <p className="animate-on-scroll text-white/55 text-sm mb-10">Six integrated steps from farm to fuel station.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/15">
              {supplyChainSteps.map((step, i) => (
                <div
                  key={i}
                  className="animate-on-scroll bg-bteal p-6 hover:bg-bteal-dark transition-colors duration-200"
                  style={{ transitionDelay: `${i * 0.07}s` }}
                >
                  <div className="flex items-center gap-3 mb-4">
                    <span className="text-4xl font-black text-white/15 leading-none">{step.step}</span>
                    <span className="w-7 h-7 flex items-center justify-center rounded-md bg-white/10">
                      <i className={`${step.icon} text-white text-xs`} />
                    </span>
                  </div>
                  <h3 className="font-semibold text-white text-sm mb-1.5">{step.title}</h3>
                  <p className="text-white/55 text-xs leading-relaxed">{step.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── SOURCING FROM FARMERS IN BIHAR ── */}
        <section className="py-16 md:py-24 bg-white">
          <div className="max-w-7xl mx-auto px-6 md:px-10">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-start">
              {/* Image col */}
              <div className="space-y-4">
                <div className="animate-on-scroll rounded-lg overflow-hidden" style={{ height: '300px' }}>
                  <img
                    src={ecoFarmer}
                    alt="Farmer sourcing in Bihar"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { label: 'West Champaran', icon: 'ri-map-pin-2-line' },
                    { label: 'East Champaran', icon: 'ri-map-pin-2-line' },
                    { label: 'Muzaffarpur', icon: 'ri-map-pin-2-line' },
                    { label: 'Gopalganj', icon: 'ri-map-pin-2-line' },
                    { label: 'Saran', icon: 'ri-map-pin-2-line' },
                    { label: 'Darbhanga', icon: 'ri-map-pin-2-line' },
                    { label: 'Supaul', icon: 'ri-map-pin-2-line' },
                    { label: 'Vaishali', icon: 'ri-map-pin-2-line' },
                  ].map((d, i) => (
                    <div key={i} className="animate-on-scroll flex items-center gap-2 p-2.5 rounded-md bg-surface border border-gray-100" style={{ transitionDelay: `${i * 0.04}s` }}>
                      <i className={`${d.icon} text-bgreen text-xs`} />
                      <span className="text-ink text-xs font-medium">{d.label}</span>
                    </div>
                  ))}
                </div>
              </div>
              {/* Text col */}
              <div>
                <span className="text-[11px] font-bold text-bgreen tracking-widest uppercase">Sourcing</span>
                <h2 className="animate-on-scroll text-3xl md:text-4xl font-bold text-ink mt-2 mb-6 tracking-tight leading-tight">
                  Sourcing from Farmers in Bihar
                </h2>
                <p className="animate-on-scroll text-ink-muted text-sm leading-relaxed mb-5">
                  Bihar&apos;s ethanol industry is built on a direct, transparent sourcing relationship with the state&apos;s farming communities. Distilleries in Bihar source sugarcane, maize, and broken rice directly from smallholder farmers across 18 key agricultural districts.
                </p>
                <p className="animate-on-scroll text-ink-muted text-sm leading-relaxed mb-8">
                  Unlike commodity markets with volatile pricing, ethanol feedstock procurement follows <strong className="text-ink">government-fixed price bands</strong> announced ahead of each supply season — giving farmers the certainty to plan, invest, and grow.
                </p>
                <div className="space-y-4">
                  {farmerBenefits.map((item, i) => (
                    <div
                      key={i}
                      className="animate-on-scroll flex items-start gap-4 p-4 rounded-lg border border-gray-100 bg-surface"
                      style={{ transitionDelay: `${i * 0.08}s` }}
                    >
                      <div className="w-9 h-9 flex items-center justify-center rounded-md flex-shrink-0" style={{ backgroundColor: `${item.color}18` }}>
                        <i className={`${item.icon} text-sm`} style={{ color: item.color }} />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-ink mb-1">{item.title}</p>
                        <p className="text-xs text-ink-muted leading-relaxed">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── AGRICULTURE & RURAL DEVELOPMENT ── */}
        <section className="py-16 md:py-24 bg-surface">
          <div className="max-w-7xl mx-auto px-6 md:px-10">
            {/* Header */}
            <div className="max-w-2xl mb-12">
              <span className="text-[11px] font-bold text-bgreen tracking-widest uppercase">Rural Impact</span>
              <h2 className="animate-on-scroll text-3xl md:text-4xl font-bold text-ink mt-2 mb-4 tracking-tight leading-tight">
                Agriculture &amp; Rural Development through Ethanol Production
              </h2>
              <p className="animate-on-scroll text-ink-muted text-sm leading-relaxed">
                Ethanol is more than fuel — it is a rural development catalyst. By anchoring industrial demand to agricultural supply, Bihar&apos;s ethanol sector creates a self-reinforcing cycle of growth for farmers, villages, and the broader rural economy.
              </p>
            </div>

            {/* Impact Stats */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-14">
              {ruralImpactStats.map((stat, i) => (
                <div
                  key={i}
                  className="animate-on-scroll p-5 rounded-lg bg-white border border-gray-100"
                  style={{ transitionDelay: `${i * 0.08}s` }}
                >
                  <div className="w-8 h-8 flex items-center justify-center rounded-md mb-3" style={{ backgroundColor: `${stat.color}18` }}>
                    <i className={`${stat.icon} text-sm`} style={{ color: stat.color }} />
                  </div>
                  <p className="text-2xl font-black text-ink leading-none mb-1" style={{ color: stat.color }}>{stat.value}</p>
                  <p className="text-[11px] text-ink-muted leading-snug">{stat.label}</p>
                </div>
              ))}
            </div>

            {/* Two col: image + points */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-start">
              <div className="animate-on-scroll rounded-lg overflow-hidden" style={{ height: '380px' }}>
                <img
                  src={ecoRuralDev}
                  alt="Rural development through ethanol production Bihar"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="space-y-5">
                {ruralDevelopmentPoints.map((point, i) => (
                  <div
                    key={i}
                    className="animate-on-scroll flex items-start gap-4 p-5 rounded-lg bg-white border border-gray-100"
                    style={{ transitionDelay: `${i * 0.08}s` }}
                  >
                    <div className="w-9 h-9 flex items-center justify-center rounded-md bg-bgreen/10 flex-shrink-0 mt-0.5">
                      <i className={`${point.icon} text-bgreen text-sm`} />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-ink mb-1.5">{point.title}</p>
                      <p className="text-xs text-ink-muted leading-relaxed">{point.desc}</p>
                    </div>
                  </div>
                ))}

                {/* Quote block */}
                <div className="animate-on-scroll p-5 rounded-lg bg-bgreen/8 border border-bgreen/15">
                  <p className="text-sm text-ink leading-relaxed italic mb-3">
                    &ldquo;Bihar&apos;s ethanol push isn&apos;t just an energy story — it&apos;s the most significant rural income intervention the state has seen in a generation.&rdquo;
                  </p>
                  <p className="text-[11px] font-semibold text-bgreen uppercase tracking-wider">— Bihar Ethanol Association</p>
                </div>
              </div>
            </div>
          </div>
        </section>

      </main>
      <Footer />
    </div>
  );
};

export default Ecosystem;
